import type { AudioLevelsData, CutterCorePlayerTarget, QueuedAudioFrame} from '@mimir/cutter/player/cutterCorePlayer';
import { createPlayer, type CutterCorePlayer } from '@mimir/cutter/player/cutterCorePlayer';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import type { Observable} from '@mimir/common/util/observable';
import { calculatedObservable, ObservableState } from '@mimir/common/util/observable';
import type { MediaSelection } from '@mimir/common/util/timeRange';
import type { FrameTime } from '@mimir/client-common/time/frameTime';
import { FRAC_ONE, type Fraction } from '@mimir/common/util/fractionType';
import { approximateFractionValue, frac, invertFraction } from '@mimir/common/util/integerMath';
import type { AudioInitializationCallback, BufferedRange, ItemMediaPlayerOps, PlayerError, PostLoadOperation } from '../itemMediaPlayerOps';
import type { AudioTrackConfig, AudioTrackConfigUpdate, PeakRange } from '../audioTrackConfig';
import type { AudioHandler } from '@mimir/cutter/player/audioHandler';
import { WorkletAudioHandler } from './audioWorkletAudioHandler';
import type { DecoderFactory, VideoDecoder, AudioDecoder, CreateVideoDecoderParameters, CreateAudioDecoderParameters, DemuxAndDecodeFrameProviderSettings } from '@mimir/cutter/frameProvider/demuxAndDecode/demuxAndDecodeFrameProvider';
import { createDemuxAndDecodeFrameProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/demuxAndDecodeFrameProvider';
import { createDummyFrameProvider } from '@mimir/cutter/frameProvider/testSignalFrameProvider';
import type { TestSignalPacketProviderSettings } from '@mimir/cutter/frameProvider/demuxAndDecode/testSignalPacketProvider';
import { createTestSignalPacketProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/testSignalPacketProvider';
import { createTestVideoDecoder } from '@mimir/cutter/frameProvider/demuxAndDecode/testVideoDecoder';
import { createTestAudioDecoder } from '@mimir/cutter/frameProvider/demuxAndDecode/testAudioDecoder';
import { createWebVideoDecoder } from '@mimir/browser/cutter/webVideoDecoder';
import { createWebAudioDecoder } from '@mimir/browser/cutter/webAudioDecoder';
import { createMimirSegmentProvider } from './mimirSegmentProvider';
import { makeBrowserFetch } from '@mimir/web/browser/browserFetch';
import { createSegmentedPacketProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import { RawFrameRenderer } from '@mimir/browser/cutter/rawFrameRenderer';
import type { Logger } from '@mimir/common/types/logger';
import { fractionToFuzzy, integerToFuzzy } from '@mimir/common/util/fuzzyMath';
import type { BufferRange, FrameInfoV2, FrameProviderV2, VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import type { BufferCache} from '@mimir/cutter/frameProvider/refCountedBuffer';
import { createBufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';
import type { Cleanable } from '@mimir/common/util/cleanable';
import { WebGlWrapper } from '@mimir/browser/gldraw/webGlWrapper';
import type { PlaybackQuality, QualityChangeState, QualityOption, QualitySelectionResult } from '../playbackQuality';

const WIDTH = 640;
const HEIGHT = 360;

interface ActiveFrameProvider {
  frameProvider: FrameProviderV2,
  bufferCache: BufferCache,
}


export class CutterItemMediaPlayer implements ItemMediaPlayerOps, CutterCorePlayerTarget {
  private readonly player : CutterCorePlayer;
  private readonly canvas : HTMLCanvasElement;
  private readonly offscreenCanvas : OffscreenCanvas;
  private readonly webGlWrapper: WebGlWrapper;
  private readonly bufferCache : BufferCache;
  private readonly renderer : RawFrameRenderer;
  private readonly audioHandler : AudioHandler;
  private readonly logger : Logger;
  private readonly frameProviderImplementation : 'fake' | 'semi-fake' | 'demux-and-decode';
  private activeFrameProvider : ActiveFrameProvider | null = null;
  private readonly cleanables : Cleanable[] = [];

  constructor(
    services: {
      logger: Logger,
    },
    parameters: {
      frameProviderImplementation: 'fake' | 'semi-fake' | 'demux-and-decode',
    },
    prematureVideoFrameReuseBugWorkaround: boolean
  ) {
    this.canvas = window.document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.position = 'absolute';
    this.offscreenCanvas = this.canvas.transferControlToOffscreen();
    this.webGlWrapper = new WebGlWrapper(services.logger, this.offscreenCanvas, prematureVideoFrameReuseBugWorkaround);
    this.frameProviderImplementation = parameters.frameProviderImplementation;
    this.audioHandler = new WorkletAudioHandler();
    this.logger = services.logger;
    this.bufferCache = createBufferCache(this.logger);
    this.renderer = new RawFrameRenderer(this.webGlWrapper);
    this.player = createPlayer(this.logger, this, createBufferCache(this.logger));
    this.virtualClipMediaPosition =
      calculatedObservable(
        (requestedTimeInMs: number | null, actualPlayerTimeInMs) => {
          // TODO: Let's not keep track of player time as ms and instead keep
          // fractions so that we can return a precise fuzzy value here
          return { approximate: (requestedTimeInMs ?? actualPlayerTimeInMs) / 1000.0 };
        },
        this.cleanables,
        this.requestedTimeInMs,
        this.actualPlayerTimeInMs,
      );

  }

  // implements CutterCorePlayerTarget

  private actualPlayerTimeInMs = new ObservableState(0);

  onFormatChanged(_info: FrameInfoV2): void {
    // we don't care about this here
  }

  displayVideoFrame(position: Fraction, videoFrame: VideoFrameV2): void {
    const rect = this.webGlWrapper.getCanvasResolution();
    this.offscreenCanvas.width = rect.width;
    this.offscreenCanvas.height = rect.height;
    this.renderer.renderFrame(videoFrame);
    this.actualPlayerTimeInMs.value = approximateFractionValue(position) * 1000.0;
  }

  onBufferedRangeChange(ranges: BufferRange[]) {
    this.buffered.value = ranges.map((range) => {
      return {
        start: approximateFractionValue(range.start) * 1000.0,
        end: approximateFractionValue(range.end) * 1000.0,
        kind: range.kind,
      };
    });
  }

  playerIdle(): void {
    this.requestedTimeInMs.value = null;
  }

  displayAudioLevels(_data: AudioLevelsData): void {
    //Comment out to dump audio levels to console
    //console.log(`audioLevels: ${JSON.stringify(data.channels.map(f => f.toString()))}`);
  }

  queueAudioFrame(audioFrame: QueuedAudioFrame): void {
    this.audioHandler.queueAudioFrame(audioFrame);
  }

  setError(error: ErrorEvent): void {
    console.log('Cutter canvas player error', error);
  }

  startAudioPlayback(): void {
    this.paused.value = false;
    this.audioHandler.startAudioPlayback();
  }

  stopAudioPlayback(clearQueue: boolean): void {
    this.paused.value = true;
    this.speedFactor.value = 0;
    this.audioHandler.stopAudioPlayback(clearQueue);
  }

  frameRateChanged(_frameRate: Fraction): void {
    //
  }

  // implements ItemMediaPlayerOps

  async loadAudio(_callbacks: AudioInitializationCallback): Promise<boolean> {
    return false;
  }

  getChannelsAsMatrix(): number[][] | null {
    return null;
  }

  play(_ms?: number): void {
    this.setSpeedFactor(1.0);
  }

  pause(): void {
    this.setSpeedFactor(0.0);
  }

  setVtt(_vttUrl: string | null): void {

  }

  seekToClampedSourceMediaPosition(sourcePos: FuzzyValue): void {
    this.seekToClampedPlayerPts(sourcePos.approximate);
  }

  seekToClampedPlayerPts(unclampledPlayerPts: number): void {
    this.requestedTimeInMs.value = unclampledPlayerPts * 1000.0;
    const position = frac(Math.floor(unclampledPlayerPts * 1000), 1000);
    this.player.setPosition({ type: 'absolute', position });
  }

  seekAndGrabImage(_sourcePos: FuzzyValue): Promise<string> {
    throw new Error('TODO');
  }

  addPlayerErrorCallback(_callback: PlayerError): void {

  }

  removePlayerErrorCallback(_callback: PlayerError): void {

  }

  private currentItemId : string | null = null;

  setItemId(
    apiActions: VideoApiActions,
    itemId: string,
    itemType: 'video' | 'audio',
    _flags: {
      useNewBufferingLogic: boolean,
      disableWebWorkersForFlacDownmix: boolean,
    },
    _callbacks?: {
      noPlayableArtifactYet?: () => void,
      postLoadOperation?: PostLoadOperation,
    },
  ): void {
    if (this.currentItemId === itemId) {
      return;
    }
    this.currentItemId = itemId;
    this.setupTestSource(apiActions, itemId, itemType);
  }

  async setupTestSource(
    apiActions: VideoApiActions,
    itemId: string,
    itemType: 'video' | 'audio',
  ) {
    let frameProviderToCleanUp : ActiveFrameProvider | null = null;
    if (this.frameProviderImplementation === 'demux-and-decode') {
      const bufferCache = this.bufferCache;

      const decoderFactory : DecoderFactory = {
        createVideoDecoder: (parameters: CreateVideoDecoderParameters): VideoDecoder => {
          return createWebVideoDecoder({
            ...parameters,
            webGlWrapper: this.webGlWrapper,
            logger: this.logger,
          });
        },
        createAudioDecoder: (parameters: CreateAudioDecoderParameters): AudioDecoder => {
          return createWebAudioDecoder({ ...parameters, logger: this.logger, bufferCache });
        },
      };

      const fetch = makeBrowserFetch(window).fetch;

      const services = {
        fetch,
        apiActions,
        logger: this.logger,
      };

      this.logger.info('Creating TS segment provider ...');

      const segmentProvider =
        createMimirSegmentProvider(services, itemId, itemType);

      const duration = await segmentProvider.getDuration();
      const durationInMs = approximateFractionValue(duration) * 1000.0;

      this.durationInMs.value = durationInMs;
      this.virtualClipMediaDurationInMs.value = durationInMs;
      this.mediaSelection.value = {
        whole: true,
        bounds: {
          start: { approximate: 0 },
          end: { approximate: durationInMs / 1000.0 },
        }
      };

      this.logger.info('Creating segment packetProvider ...');

      const packetProvider =
        await createSegmentedPacketProvider(this.logger, segmentProvider);

      this.logger.info('Creating frame provider ...');

      const settings : DemuxAndDecodeFrameProviderSettings = {
        forwardCacheDuration: FRAC_ONE,
        rewindCacheDuration: FRAC_ONE,
      };


      const frameProvider =
        createDemuxAndDecodeFrameProvider({
          logger: this.logger,
          packetProvider,
          decoderFactory,
          settings,
          bufferCache,
        });

      const info = await frameProvider.getInfo(AbortSignal.any([]));

      this.sampleDuration = invertFraction(info.frameRate);

      this.logger.info('Setting as frame provider');

      this.player.setDuration(duration);
      this.player.setFrameProvider(frameProvider);

      frameProviderToCleanUp = this.activeFrameProvider;
      this.activeFrameProvider = { frameProvider, bufferCache };

      this.logger.info('Setup complete');
    }

    if (this.frameProviderImplementation === 'fake') {
      const bufferCache = this.bufferCache;
      const frameProvider =
        createDummyFrameProvider(
          {
            width: WIDTH,
            height: HEIGHT,
          },
          {
            numerator: 50,
            denominator: 1,
          },
          48000,
          bufferCache,
        );
      this.player.setFrameProvider(frameProvider);

      frameProviderToCleanUp = this.activeFrameProvider;
      this.activeFrameProvider = { frameProvider, bufferCache };
    }

    if (this.frameProviderImplementation === 'semi-fake') {
      const bufferCache = this.bufferCache;
      const durationInMs = 10000;
      const packetProviderSettings : TestSignalPacketProviderSettings = {
        resolution: {
          width: WIDTH,
          height: HEIGHT,
        },
        frameRate: {
          numerator: 50,
          denominator: 1,
        },
        sampleRate: 48000,
        gopSize: 50,
        duration: frac(durationInMs, 1000),
        samplesPerPacket: 1024,
        bufferCache,
      };
      const decoderFactory : DecoderFactory = {
        createVideoDecoder: (parameters: CreateVideoDecoderParameters): VideoDecoder => {
          const { codec } = parameters;
          if (codec !== 'test_video_codec') {
            throw new Error(`No decoder available for video codec: ${codec}`);
          }
          return createTestVideoDecoder(this.logger, bufferCache, { emitDecodedFramesInPresentationOrder: true });
        },
        createAudioDecoder: (parameters: CreateAudioDecoderParameters): AudioDecoder => {
          const { codec } = parameters;
          if (codec !== 'test_audio_codec') {
            throw new Error(`No decoder available for audio codec: ${codec}`);
          }
          return createTestAudioDecoder(this.logger, bufferCache, { emulateAac: false, allowAudioDiscontinuities: false });
        },
      };
      const abortController = new AbortController();
      const abortSignal = abortController.signal;
      const packetProvider = await createTestSignalPacketProvider(packetProviderSettings, abortSignal);

      const settings : DemuxAndDecodeFrameProviderSettings = {
        forwardCacheDuration: FRAC_ONE,
        rewindCacheDuration: FRAC_ONE,
      };

      const frameProvider =
        createDemuxAndDecodeFrameProvider({
          logger:  this.logger,
          packetProvider,
          decoderFactory,
          settings,
          bufferCache
        });

      this.player.setFrameProvider(frameProvider);
      this.durationInMs.value = durationInMs;
      this.virtualClipMediaDurationInMs.value = durationInMs;
      this.mediaSelection.value = {
        whole: true,
        bounds: {
          start: { approximate: 0 },
          end: { approximate: durationInMs / 1000.0 },
        }
      };

      frameProviderToCleanUp = this.activeFrameProvider;
      this.activeFrameProvider = { frameProvider, bufferCache };
    }

    this.seekToVirtualClipMediaPosition(integerToFuzzy(0));
    this.virtualClipStart.value = { approximate: 0 };
    this.buffered.value = [{ start: 0, end: 5000 }];
    this.hasMedia.value = true;

    if (frameProviderToCleanUp !== null) {
      this.logger.info('Cleaning up previous frame provider');
      frameProviderToCleanUp.frameProvider.close();
      this.logger.info(`Cleaned up previous frame provider. buffer stats: ${frameProviderToCleanUp.bufferCache.stats()}`);
    }
  }

  clear(): void {

  }

  togglePlayPause(): void {
    if (this.paused.value) {
      this.play();
    } else {
      this.pause();
    }
  }

  setVolume(_volume: number): void {

  }

  setMuted(_val: boolean): void {

  }

  appendVideoElementToElement(parent: Element): void {
    parent.append(this.canvas);
  }

  updateAudioTrackConfig(_update: AudioTrackConfigUpdate[]): void {

  }

  setPosterImage(_src: string): void {

  }

  setSpeedFactor(speedFactor: number): void {
    this.speedFactor.value = speedFactor;
    this.player.setPlaybackRate(frac(Math.round(speedFactor * 4.0), 4));
  }

  toggleFullscreen(): void {

  }

  seekToVirtualClipMediaPosition(virtualClipMediaPosition: FuzzyValue): void {
    // TODO: Do not use approximate here
    this.seekToClampedPlayerPts(virtualClipMediaPosition.approximate);
  }

  goForward(_ms: number): void {

  }

  goBackward(_ms: number): void {

  }

  goForwardBySecond(): void {

  }

  goForwardByTenSeconds(): void {

  }

  goBackwardBySecond(): void {

  }

  goBackwardByTenSeconds(): void {

  }

  goFrameBackward(): void {
    this.player.stepBackward();
  }

  goFrameForward(): void {
    this.player.stepForward();
  }

  getPeakSeriesCount(_trackIndex: number): number {
    return 0;
  }

  getPeaksForSourceMediaRange(_trackIndex: number, _width: number, _channel: number, _range: PeakRange): number[] {
    return [];
  }

  private sourceMediaPosition = new ObservableState<FuzzyValue | null>(null);
  getSourceMediaPositionObs(): Observable<FuzzyValue | null> {
    return this.sourceMediaPosition.getObservable();
  }

  private sampleDuration : Fraction | null = null;
  getSampleDuration(): FuzzyValue | null {
    return this.sampleDuration ? fractionToFuzzy(this.sampleDuration) : null;
  }

  private virtualClipStart = new ObservableState<FuzzyValue | null>(null);
  getVirtualClipStartObs(): Observable<FuzzyValue | null> {
    return this.virtualClipStart.getObservable();
  }

  private mediaSelection = new ObservableState<MediaSelection>({
    whole: true,
    bounds: {
      start: { approximate: 0 },
      end: { approximate: 0 },
    }
  });

  getVirtualClipSourceMediaRangeObs(): Observable<MediaSelection> {
    return this.mediaSelection.getObservable();
  }

  private requestedTimeInMs = new ObservableState<number | null>(null);


  private durationInMs = new ObservableState<number>(0);
  private virtualClipMediaPosition : Observable<FuzzyValue>;
  private paused = new ObservableState<boolean>(true);
  private speedFactor = new ObservableState<number>(0.0);
  private visible = new ObservableState<boolean>(false);
  private hasMedia = new ObservableState<boolean>(false);
  private loading = new ObservableState<boolean>(false);
  private virtualClipMediaDurationInMs = new ObservableState<number>(0);
  private hasAudioChannels = new ObservableState<boolean>(false);
  private videoAspectRatio = new ObservableState<number | null>(null);
  private playerAspectRatio = new ObservableState<number | null>(null);
  private seeking = new ObservableState<boolean>(false);
  private currentFrameTime = new ObservableState<FrameTime | null>(null);
  private lastError = new ObservableState<ErrorEvent | null>(null);
  private buffered = new ObservableState<BufferedRange[]>([]);
  private hasGrowingMedia = new ObservableState<boolean>(false);
  private audioTrackConfigs = new ObservableState<AudioTrackConfig[]>([]);
  private peakCount = new ObservableState<number>(0);
  private isLive = new ObservableState<boolean>(false);

  getSourceMediaDurationObs(): Observable<FuzzyValue> {
    throw new Error('TODO');
  }

  getVirtualClipDurationObs(): Observable<FuzzyValue> {
    throw new Error('TODO');
  }

  getVirtualClipMediaPositionObs() { return this.virtualClipMediaPosition; }
  getPausedObs() { return this.paused.getObservable(); }
  getSpeedFactorObs() { return this.speedFactor.getObservable(); }
  getVisibleObs() { return this.visible.getObservable(); }
  getHasMediaObs() { return this.hasMedia.getObservable(); }
  getLoadingObs() { return this.loading.getObservable(); }
  getVirtualClipMediaDurationInMsObs() { return this.virtualClipMediaDurationInMs.getObservable(); }
  getHasAudioChannelsObs() { return this.hasAudioChannels.getObservable(); }
  getVideoAspectRatioObs() { return this.videoAspectRatio.getObservable(); }
  getPlayerAspectRatioObs() { return this.playerAspectRatio.getObservable(); }
  getSeekingObs() { return this.seeking.getObservable(); }
  getCurrentFrameTimeObs() { return this.currentFrameTime.getObservable(); }
  getLastErrorObs() { return this.lastError.getObservable(); }
  getBufferedObs() { return this.buffered.getObservable(); }
  getHasGrowingMediaObs() { return this.hasGrowingMedia.getObservable(); }
  getAudioTrackConfigsObs() { return this.audioTrackConfigs.getObservable(); }
  getPeakCountObs() { return this.peakCount.getObservable(); }
  getIsLiveObs() { return this.isLive.getObservable(); }

  // Quality selection methods - not supported in cutter player
  private qualityChangeState = new ObservableState<QualityChangeState>({ inProgress: false });
  async changeQuality(
    _quality: PlaybackQuality,
    flags: {
      useNewBufferingLogic: boolean,
    }
  ): Promise<QualitySelectionResult> {
    return {
      success: false,
      quality: _quality,
      error: 'Quality selection is not supported in cutter player',
    };
  }
  getAvailableQualities(): QualityOption[] {
    return [];
  }
  getCurrentQuality(): PlaybackQuality | null {
    return null;
  }
  getQualityChangeStateObs(): Observable<QualityChangeState> {
    return this.qualityChangeState.getObservable();
  }

  destroy(): void {
    this.audioHandler.close();
  }
}


